import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Ресурсы
import chaina from "../assets/chaina.webp";
import background from "../assets/background.webp";

const More_Info = () => {
  const { t } = useTranslation();
  const list = t("wellcomelist", { returnObjects: true });

  const [isAnimateStart, setIsAnimateStart] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeSystem, setActiveSystem] = useState("ongrid");

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimateStart(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Данные о системах
  const systems = {
    ongrid: {
      id: "ongrid",
      title: t("ongrid_title"),
      description: t("ongrid_description"),
      advantages: [t("ongrid_adv1"), t("ongrid_adv2"), t("ongrid_adv3")],
      disadvantages: [t("ongrid_disadv1")],
      suitable: t("ongrid_suitable_text"),
      color: "from-yellow-500 to-orange-500",
      keyFeature: t("ongrid_key_feature"),
    },
    offgrid: {
      id: "offgrid",
      title: t("offgrid_title"),
      description: t("offgrid_description"),
      advantages: [t("offgrid_adv1"), t("offgrid_adv2")],
      disadvantages: [t("offgrid_disadv1"), t("offgrid_disadv2")],
      suitable: t("offgrid_suitable_text"),
      color: "from-green-500 to-teal-500",
      keyFeature: t("offgrid_key_feature"),
    },
    hybrid: {
      id: "hybrid",
      title: t("hybrid_title"),
      description: t("hybrid_description"),
      advantages: [t("hybrid_adv1"), t("hybrid_adv2"), t("hybrid_adv3")],
      disadvantages: [t("hybrid_disadv1")],
      suitable: t("hybrid_suitable_text"),
      color: "from-purple-500 to-pink-500",
      keyFeature: t("hybrid_key_feature"),
    },
  };

  const currentSystem = systems[activeSystem];

  return (
    <section
      className="relative w-full h-auto py-[100px] md:py-[170px] bg-slate-800 flex flex-row justify-center items-center overflow-hidden transition-all duration-[1200ms] ease-out  bg-center bg-fixed"
      style={{
        opacity: isAnimateStart ? "1" : "0",
        transform: isAnimateStart ? "translateY(0)" : "translateY(50px)",
        backgroundImage: isAnimateStart ? `url(${background})` : "none",
      }}
    >
      <div className="max-w-[1400px] w-full mx-auto px-6">
        {/* Заголовок секции */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-['Cormorant_Garamond'] text-white uppercase text-[28px] sm:text-[36px] md:text-[48px] tracking-[0.1em]">
            {t("solar_systems_title")}
          </h2>
          <div
            className={`h-1 bg-amber-500 mx-auto transition-all duration-1000 delay-500 ${isAnimateStart ? "w-24 md:w-32" : "w-0"}`}
          />
          <p className="text-white/70 mt-4 max-w-2xl mx-auto text-sm md:text-base">
            {t("solar_systems_subtitle")}
          </p>
        </div>

        {/* Кнопки переключения между системами */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-10 md:mb-14">
          {Object.values(systems).map((system) => (
            <button
              key={system.id}
              onClick={() => setActiveSystem(system.id)}
              className={`
               py-2 px-3 font-bold rounded-lg text-sm md:text-base
                transition-all duration-300 transform hover:scale-105
                flex items-center gap-2
                ${
                  activeSystem === system.id
                    ? `bg-gradient-to-r ${system.color} text-white shadow-lg`
                    : "bg-white/10 text-white/70 hover:bg-white/20 backdrop-blur-sm"
                }
              `}
            >
              <span className="text-xl md:text-2xl">{system.icon}</span>
              <span className="hidden sm:inline">
                {system.title.split(" ").slice(1).join(" ")}
              </span>
              <span className="sm:hidden">{system.id.toUpperCase()}</span>
            </button>
          ))}
        </div>

        {/* Контент - фото всегда на месте, меняется только текст */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Левая колонка - информация (меняется при клике) */}
          <div className="space-y-6 transition-all duration-500">
            {/* Заголовок */}
            <div className="bg-gradient-to-r from-white/10 to-transparent p-6 rounded-2xl border-l-4 border-amber-500">
              <h3 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl md:text-4xl">
                  {currentSystem.icon}
                </span>
                {currentSystem.title}
              </h3>
            </div>

            {/* Описание */}
            <div className="bg-black/20 backdrop-blur-md p-6 rounded-2xl">
              <p className="text-white/90 leading-relaxed text-sm md:text-base">
                {currentSystem.description}
              </p>
            </div>

            {/* Преимущества */}
            <div className="bg-gradient-to-r from-green-500/10 to-transparent p-6 rounded-2xl border-l-4 border-green-500">
              <h4 className="text-green-400 font-bold text-lg mb-4 flex items-center gap-2">
                <span>✓</span> {t("ongrid_advantages")}
              </h4>
              <ul className="space-y-3">
                {currentSystem.advantages.map((adv, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 text-white/80 text-sm md:text-base"
                  >
                    <span className="text-green-400 mt-1">▹</span>
                    {adv}
                  </li>
                ))}
              </ul>
            </div>

            {/* Недостатки */}
            <div className="bg-gradient-to-r from-red-500/10 to-transparent p-6 rounded-2xl border-l-4 border-red-500">
              <h4 className="text-red-400 font-bold text-lg mb-4 flex items-center gap-2">
                <span>⚠</span> {t("ongrid_disadvantages")}
              </h4>
              <ul className="space-y-3">
                {currentSystem.disadvantages.map((disadv, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 text-white/80 text-sm md:text-base"
                  >
                    <span className="text-red-400 mt-1">▹</span>
                    {disadv}
                  </li>
                ))}
              </ul>
            </div>

            {/* Для кого подходит */}
            <div className="bg-gradient-to-r from-blue-500/10 to-transparent p-6 rounded-2xl border-l-4 border-blue-500">
              <h4 className="text-blue-400 font-bold text-lg mb-3 flex items-center gap-2">
                <span>🏠</span> {t("ongrid_suitable")}
              </h4>
              <p className="text-white/80 text-sm md:text-base leading-relaxed">
                {currentSystem.suitable}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="relative">
              <div
                className={`relative z-10 overflow-hidden rounded-3xl transition-all duration-[1.5s] ease-out ${isAnimateStart ? "scale-100 opacity-100" : "scale-110 opacity-0"}`}
              >
                <img
                  src={chaina}
                  className="w-full h-auto min-h-[250px] md:min-h-[400px] object-cover shadow-2xl"
                  alt="Solar Holding"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/40 to-transparent" />
              </div>
              <div className="absolute -top-3 -right-3 md:-top-4 md:-right-4 w-full h-full border-2 border-amber-500 rounded-3xl -z-0 opacity-30" />
            </div>

            {/* Ключевая особенность (меняется при клике) */}
            <div className="bg-amber-500/20 backdrop-blur-md p-6 rounded-2xl text-center transition-all duration-500">
              <div className="text-4xl md:text-5xl mb-3">
                {currentSystem.icon}
              </div>
              <p className="text-white font-semibold text-sm md:text-base">
                {currentSystem.keyFeature}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-black/20 backdrop-blur-md mt-6 p-8 rounded-2xl border-l-4 border-amber-500 text-white/90 font-['Raleway'] space-y-6 text-[15px] leading-relaxed">
          <p className="font-bold text-amber-500 text-lg border-b border-white/10 pb-2">
            {t("exhibition_title")}
          </p>

          <p>{t("exhibition_text_1")}</p>

          <p>{t("exhibition_text_2")}</p>

          <p className="bg-amber-500/10 p-4 rounded-lg italic">
            {t("exhibition_text_3")}
          </p>

          <p>{t("exhibition_text_4")}</p>
        </div>
      </div>

      {/* Добавляем анимацию */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default More_Info;
